import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationService } from 'primeng/api';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Observable, Subscription } from 'rxjs';
import { FINAL_USERS_COLUMNS, FINAL_USERS_PAGE_DEFAULT_SIZE, FINAL_USERS_PAGE_SIZE_OPTIONS, FINAL_USERS_PROFILES_TABLE_HEADER } from 'src/app/conf/final-users';
import { Device } from 'src/app/model/device';
import { Profile } from 'src/app/model/profile';
import { User } from 'src/app/model/user';
import { AppState } from 'src/app/store/app.reducer';
import * as DeviceActions from 'src/app/store/device/device.actions';
import * as UserActions from 'src/app/store/user/user.actions';
import { selectCurrentDevice, selectCurrentDeviceFinalUserProfiles, selectCurrentDeviceFinalUsers } from 'src/app/store/device/device.selector';
import * as UserFileActions from 'src/app/store/user-file/user-file.actions';
import { ExportFinalUsersDialogComponent } from '../export-final-users-dialog/export-final-users-dialog.component';
import { StandardPageLayout } from 'src/app/modules/shared/stdlayoutlib/classes.stdlayoutlib';
import { EDeviceCategory } from 'src/app/conf/device-categories';
import { EDeviceModel } from 'src/app/conf/device-models';
import { selectExportingFinalUsersList, selectDeletingFinalUsers, selectDeletedFinalUsers, selectImportingFinalUsers, selectImportedFinalUsers } from 'src/app/store/user-file/user-file.selector';
import { selectIsImpersMode, selectUser } from 'src/app/store/user/user.selector';

@Component({
	selector: 'app-final-users-profiles',
	templateUrl: './final-users-profiles.component.html',
	providers: [ConfirmationService, DialogService]
})

export class FinalUsersProfilesComponent implements OnInit, OnDestroy {
	currentDeviceFinalUsers$: Observable<User[] | undefined>;
	currentDeviceFinalUsersSub: Subscription | undefined;
	deviceFinalUserProfiles$: Observable<Map<number, Profile[]> | undefined>;
	deviceFinalUserProfilesSub: Subscription | undefined;

	usersColumns = FINAL_USERS_COLUMNS;
	displayedColumns = FINAL_USERS_PROFILES_TABLE_HEADER;
	pageSizeOptions = FINAL_USERS_PAGE_SIZE_OPTIONS;
	pageSize = FINAL_USERS_PAGE_DEFAULT_SIZE;
	totalRecords = 0;

	device: Device;
	device$: Observable<Device | undefined>;
	deviceSub: Subscription | undefined;

	exporting: boolean;
	exporting$: Observable<boolean | undefined>;
	exportingSub: Subscription | undefined;

	deleted: boolean;
	deleted$: Observable<boolean | undefined>;
	deletedSub: Subscription | undefined;

	deleting: boolean;
	deleting$: Observable<boolean | undefined>;
	deletingSub: Subscription | undefined;

	importing: boolean;
	importing$: Observable<boolean | undefined>;
	importingSub: Subscription;

	currentUser: User;
	currentUser$: Observable<User | undefined>;
	currentUserSub: Subscription | undefined;

	isImpersMode: boolean;
	isImpersMode$: Observable<boolean | undefined>;
	isImpersModeSub: Subscription | undefined;

	imported: boolean;
	imported$: Observable<boolean | undefined>;
	importedSub: Subscription;

	finalUserProfiles: Map<number, Profile[]>;
	deviceFinalUsers: User[];

	acceptButtonStyle: string;
	rejectButtonStyle: string;

	screenWidth: number;

	ref: DynamicDialogRef | undefined;

	constructor(
		private store: Store<AppState>,
		private translate: TranslateService,
		public confirmationService: ConfirmationService,
		public dialogService: DialogService
	) {
		this.initSelectors();
	}

	initSelectors(): void {
		// Store selectors
		this.currentDeviceFinalUsers$ = this.store.select(selectCurrentDeviceFinalUsers);
		this.deviceFinalUserProfiles$ = this.store.select(selectCurrentDeviceFinalUserProfiles);
		this.device$ = this.store.select(selectCurrentDevice);
		this.exporting$ = this.store.select(selectExportingFinalUsersList);
		this.deleting$ = this.store.select(selectDeletingFinalUsers);
		this.deleted$ = this.store.select(selectDeletedFinalUsers);
		this.importing$ = this.store.select(selectImportingFinalUsers);
		this.imported$ = this.store.select(selectImportedFinalUsers);
		this.currentUser$ = this.store.select(selectUser);
		this.isImpersMode$ = this.store.select(selectIsImpersMode);
	}

	ngOnInit(): void {
		this.store.dispatch(DeviceActions.getFinalUsers());
		this.currentDeviceFinalUsersSub = this.currentDeviceFinalUsers$.subscribe((deviceFinalUsers) => {
			this.deviceFinalUsers = deviceFinalUsers;
		});
		this.deviceFinalUserProfilesSub = this.deviceFinalUserProfiles$.subscribe((finalUserProfiles) => {
			this.finalUserProfiles = finalUserProfiles;
		});
		this.deviceSub = this.device$.subscribe((device) => {
			this.device = device;
		});
		this.exportingSub = this.exporting$.subscribe((exporting) => {
			this.exporting = exporting;
		});
		this.deletingSub = this.deleting$?.subscribe((deleting) => {
			this.deleting = deleting;
		});
		this.deletedSub = this.deleted$.subscribe((deleted) => {
			if (deleted) {
				this.store.dispatch(DeviceActions.getFinalUsers());
				this.store.dispatch(UserFileActions.resetFinalUsersList());
			}
		});
		this.importingSub = this.importing$?.subscribe(
			(importing) => {
				this.importing = importing;
			}
		);
		this.importedSub = this.imported$?.subscribe(
			(imported) => {
				if (imported) {
					this.store.dispatch(DeviceActions.getFinalUsers());
					this.store.dispatch(UserFileActions.resetFinalUsersList());
				}
			}
		);
		this.currentUserSub = this.currentUser$?.subscribe(currentUser => {
			this.currentUser = currentUser;
		});
		this.isImpersModeSub = this.isImpersMode$?.subscribe(
			(isImpersMode) => {
				this.isImpersMode = isImpersMode;
			}
		);
		this.onResize();
	}

	ngOnDestroy(): void {
		this.currentDeviceFinalUsersSub?.unsubscribe();
		this.deviceFinalUserProfilesSub?.unsubscribe();
		this.deviceSub?.unsubscribe();
		this.exportingSub?.unsubscribe();
		this.deletingSub?.unsubscribe();
		this.deletedSub?.unsubscribe();
		this.importingSub?.unsubscribe();
		this.importedSub?.unsubscribe();
		this.currentUserSub?.unsubscribe();
		this.isImpersModeSub?.unsubscribe();
	}

	onUserClicked(expandedElement: boolean, user: User): void {
		if (user.Id) {
			if (expandedElement) {
				this.store.dispatch(DeviceActions.getUserProfiles({ userId: user.Id }));
			} else {
				this.store.dispatch(DeviceActions.clearUserProfiles({ userId: user.Id }));
			}
		}
	}

	onUserProfileChanged(event: any, userId: number, profile: Profile) {
		this.store.dispatch(DeviceActions.updateUserProfile({ userId, id: profile.Id, checked: event.checked }));
	}

	checkUserId(userId): boolean {
		if (userId !== undefined && userId !== null) {
			return true;
		}
		return false;
	}

	deleteUser(user: User) {
		this.acceptButtonStyle = "p-button-danger";
		this.rejectButtonStyle = "p-button-secondary";
		this.confirmationService.confirm({
			header: this.translate.instant('CONFIRMATION_REQUIRED'),
			message: this.translate.instant('ARE_YOU_SURE'),
			acceptLabel: this.translate.instant('CONFIRM'),
			rejectLabel: this.translate.instant('CANCEL'),
			icon: 'pi cm-alert',
			accept: () => {
				if (user.Id) {
					// delete final user
					this.store.dispatch(DeviceActions.removeShareDevice({ userId: user.Id }));
				} else {
					// revoke invitation
					this.store.dispatch(UserActions.revokeShareInvitation({ email: user.Email }));
				}
			},
			reject: () => { }
		});
	}

	includeCategories(categories: number[]): boolean {
		return categories.includes(this.device.CategoryId);
	}

	hasImportFinalUsers(): boolean {
		// Categories are included but modelId not supported
		if (this.device.ModelId === EDeviceModel.QBE_MATTER || this.device.ModelId === EDeviceModel.QBE_DONT_AUTOMATION) {
			return false;
		}
		return this.device.CurrentlyOwned && this.includeCategories([EDeviceCategory.Gateway, EDeviceCategory.AlarmSystem, EDeviceCategory.HomeAutomation]);
	}

	exportListFinalUser(): void {
		this.store.dispatch(UserFileActions.resetFinalUsersListDialog());
		this.ref = this.dialogService.open(ExportFinalUsersDialogComponent, {
			header: this.translate.instant('EXPORT_LIST_FINAL_USERS'),
			width: '70vw',
			breakpoints: { '1120px': '98vw' },
			contentStyle: { overflow: 'auto' },
		});
	}

	deleteFinalUsers(): void {
		this.acceptButtonStyle = "p-button-danger";
		this.rejectButtonStyle = "p-button-secondary";
		this.confirmationService.confirm({
			header: this.translate.instant('DELETE_LIST'),
			message: this.translate.instant('DELETE_LIST_WARNING'),
			acceptLabel: this.translate.instant('CONFIRM'),
			rejectLabel: this.translate.instant('CANCEL'),
			icon: 'pi cm-alert',
			accept: () => {
				if (this.isImpersMode) {
					this.store.dispatch(UserFileActions.deleteFinalUsers({ userId: this.currentUser.Id }));
				} else {
					this.store.dispatch(UserFileActions.deleteFinalUsers({}));
				}
			},
			reject: () => { }
		});
	}

	// Design
	@HostListener('window:resize', ['$event'])
	onResize() {
		this.screenWidth = window.innerWidth;
	}

	onExtendedMobile(): boolean {
		return StandardPageLayout.onExtendedMobile(this.screenWidth);
	}
}